import { useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, DownloadSimple, CheckCircle, WarningCircle, LinkedinLogo, Brain, X } from "@phosphor-icons/react";
import { RatingBadge } from "./RatingBadge";

const SIZES = [["1,10", "1–10"], ["11,50", "11–50"], ["51,200", "51–200"], ["201,500", "201–500"],
  ["501,1000", "501–1k"], ["1001,5000", "1k–5k"], ["5001,10000", "5k–10k"], ["10001", "10k+"]];
const LOC_PRESETS = ["United Kingdom", "Ireland", "United States", "Germany", "France", "Netherlands", "Switzerland", "Canada"];

export const ApolloTitleSearchPanel = ({ onCreditsUsed }) => {
  const [titlesText, setTitlesText] = useState("");
  const [locs, setLocs] = useState(new Set(["United Kingdom"]));
  const [locInput, setLocInput] = useState("");
  const [sizes, setSizes] = useState(new Set());
  const [hcOnly, setHcOnly] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [excluded, setExcluded] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [hideKnown, setHideKnown] = useState(true);
  const [ratings, setRatings] = useState({});
  const [rateBusy, setRateBusy] = useState(false);

  const titles = titlesText.split(",").map(s => s.trim()).filter(Boolean);
  const reqBody = (p) => ({ titles, locations: [...locs], employee_ranges: [...sizes], keywords: hcOnly ? "healthcare" : "", page: p, per_page: 100 });

  const search = async () => {
    if (!titles.length) { toast.error("Enter at least one job-title phrase"); return; }
    setLoading(true); setError("");
    try {
      const r = await api.apolloTitleSearch(reqBody(1));
      setRows(r.people); setTotal(r.total_entries); setExcluded(r.excluded_count || 0);
      setPage(1); setSel(new Set()); setRatings({}); setSearched(true);
      onCreditsUsed?.();
    } catch (err) { setError(fmtErr(err)); } finally { setLoading(false); }
  };

  const loadMore = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.apolloTitleSearch(reqBody(page + 1));
      setRows(prev => [...prev, ...r.people]); setTotal(r.total_entries);
      setExcluded(prev => prev + (r.excluded_count || 0)); setPage(p => p + 1);
    } catch (err) { setError(fmtErr(err)); } finally { setLoading(false); }
  };

  const addLoc = (v) => { const l = v.trim(); if (l) setLocs(prev => new Set(prev).add(l)); setLocInput(""); };
  const dropLoc = (l) => setLocs(prev => { const n = new Set(prev); n.delete(l); return n; });

  const eligible = rows.filter(r => !r.imported && !r.in_db && !r.likely_in_db && r.has_email);
  const knownCount = rows.filter(r => !r.has_email || r.likely_in_db || r.in_db).length;
  const visible = hideKnown ? rows.filter(r => r.has_email && !r.likely_in_db && !r.in_db) : rows;
  const allSel = eligible.length > 0 && eligible.every(r => sel.has(r.id));
  const toggle = (id) => setSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const rate = async () => {
    const people = visible.filter(r => r.title).map(r => ({ key: r.id, name: `${r.first_name} ${r.last_name}`.trim(), title: r.title, org: r.organization }));
    if (!people.length) { toast.error("No visible people with job titles to rate"); return; }
    setRateBusy(true);
    try {
      let rated = 0;
      for (let i = 0; i < people.length; i += 50) {
        const rr = await api.fitscanRate({ domain: "", people: people.slice(i, i + 50) });
        setRatings(prev => ({ ...prev, ...rr.ratings }));
        rated += rr.rated || 0;
      }
      toast.success(`Rated ${rated} against the Medicys ICP`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setRateBusy(false); }
  };

  const doImport = async () => {
    const chosen = rows.filter(r => sel.has(r.id) && !r.imported);
    if (!chosen.length) return;
    const toReveal = chosen.filter(r => !r.revealed_email).length;
    const cachedN = chosen.length - toReveal;
    if (!window.confirm(`Reveal and import ${chosen.length} ${chosen.length === 1 ? "person" : "people"} from Apollo? ~${toReveal} Apollo credit${toReveal === 1 ? "" : "s"} will be used${cachedN ? ` (${cachedN} already revealed — free)` : ""}.`)) return;
    setImporting(true);
    try {
      const r = await api.apolloRevealImport({ company: "", domain: "", people: chosen.map(c => ({ id: c.id })) });
      const sk = r.skipped || {};
      toast.success(`Imported ${r.created} to Contacts (unverified) · ${r.credits_charged} Apollo credit${r.credits_charged === 1 ? "" : "s"} used${sk.already_in_db ? ` · ${sk.already_in_db} already in MediHub` : ""}${r.not_found ? ` · ${r.not_found} had no usable email at Apollo` : ""}`);
      const done = new Set(chosen.map(c => c.id));
      setRows(prev => prev.map(row => done.has(row.id) ? { ...row, imported: true } : row));
      setSel(new Set());
      onCreditsUsed?.();
    } catch (err) { toast.error(fmtErr(err)); } finally { setImporting(false); }
  };

  const statusCell = (r) => {
    if (r.imported) return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> imported</span>;
    if (r.in_db) return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={13} weight="fill" /> in MediHub</span>;
    if (r.likely_in_db) return <span className="inline-flex items-center gap-1 text-amber-600" title="Someone with this first name, last initial and company is already in MediHub"><WarningCircle size={13} /> may already be in MediHub</span>;
    if (!r.has_email) return <span className="inline-flex items-center gap-1 text-amber-600"><WarningCircle size={13} /> no email at Apollo</span>;
    return <span className="text-violet-700 font-medium">new</span>;
  };

  return (
    <div className="rounded-lg border border-violet-200 bg-white" data-testid="apollo-title-panel">
      <form onSubmit={(e) => { e.preventDefault(); search(); }} className="p-3 space-y-2.5">
        <div>
          <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">1 · Job title contains… <span className="normal-case font-normal text-zinc-400">(comma-separate several phrases — Apollo also matches close variants)</span></label>
          <input value={titlesText} onChange={e => setTitlesText(e.target.value)} data-testid="title-search-input"
            placeholder="e.g. patient outcomes, patient recruitment, feasibility"
            className="mt-1 w-full h-9 rounded-md border border-zinc-200 px-2.5 text-sm focus:outline-none focus:border-violet-400" />
        </div>
        <div>
          <div className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mb-1">2 · Locations <span className="normal-case font-normal text-zinc-400">(optional — none = worldwide)</span></div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {[...locs].map(l => (
              <span key={l} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-zinc-950 text-white" data-testid={`title-loc-${l.toLowerCase().replace(/\W+/g, "-")}`}>
                {l}<button type="button" onClick={() => dropLoc(l)} className="hover:text-rose-300"><X size={11} /></button>
              </span>
            ))}
            {LOC_PRESETS.filter(l => !locs.has(l)).map(l => (
              <button key={l} type="button" onClick={() => addLoc(l)}
                className="text-xs px-2 py-1 rounded-full border border-zinc-200 text-zinc-600 hover:border-zinc-400">{l}</button>
            ))}
            <input value={locInput} onChange={e => setLocInput(e.target.value)} data-testid="title-loc-input"
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addLoc(locInput); } }}
              placeholder="other country/city + Enter" className="h-7 w-44 rounded-md border border-zinc-200 px-2 text-xs focus:outline-none focus:border-zinc-400" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider mr-1">Company size</span>
          {SIZES.map(([v, label]) => {
            const on = sizes.has(v);
            return (
              <button key={v} type="button" data-testid={`title-size-${v.replace(",", "-")}`}
                onClick={() => setSizes(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; })}
                className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>{label}</button>
            );
          })}
          <span className="text-xs text-zinc-400">none = any size</span>
          <label className="ml-3 inline-flex items-center gap-1 text-xs text-zinc-500 cursor-pointer" title="Only people whose Apollo profile mentions healthcare">
            <input type="checkbox" checked={hcOnly} onChange={() => setHcOnly(v => !v)} data-testid="title-hc-toggle" /> healthcare profiles only
          </label>
        </div>
        <button type="submit" disabled={loading || !titles.length} data-testid="title-search-btn"
          className="h-9 px-4 rounded-md bg-violet-700 text-white text-sm font-medium hover:bg-violet-800 disabled:opacity-40 flex items-center gap-1.5">
          <Sparkle size={14} weight="fill" />{loading && !searched ? "Searching Apollo…" : "3 · Search Apollo (free)"}
        </button>
      </form>

      {error && <div className="px-3 py-2 text-sm text-rose-700 bg-rose-50 border-t border-rose-100" data-testid="title-search-error">{error}</div>}

      {searched && !error && (
        <div className="border-t border-violet-100">
          <div className="px-3 py-2 text-sm text-zinc-600 border-b border-zinc-100" data-testid="title-search-summary">
            <span className="font-medium text-zinc-900">{Number(total).toLocaleString()}</span> people match across all companies — searching is free, you pay ~1 Apollo credit per email you reveal.
            {excluded > 0 && <span className="text-rose-500"> {excluded} hidden by your exclusion list.</span>}
            {knownCount > 0 && (
              <button type="button" onClick={() => setHideKnown(v => !v)} data-testid="title-toggle-known"
                className="ml-2 text-xs text-zinc-400 underline hover:text-zinc-600">
                {hideKnown ? `${knownCount} hidden (no email / already in MediHub) — show` : `hide ${knownCount} (no email / already in MediHub)`}
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 border-b border-zinc-200"><tr>
              <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSel} data-testid="title-select-all"
                onChange={() => setSel(allSel ? new Set() : new Set(eligible.map(r => r.id)))} /></th>
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">Job title</th>
              <th className="text-left px-3 py-2 font-medium">Company</th>
              <th className="text-left px-3 py-2 font-medium">Email</th>
              <th className="text-left px-3 py-2 font-medium w-10">In</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
            </tr></thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.id} className={`border-b border-zinc-100 ${r.imported || r.in_db ? "opacity-60" : ""}`} data-testid={`title-row-${i}`}>
                  <td className="px-3 py-2"><input type="checkbox" disabled={r.imported || r.in_db || !r.has_email} checked={sel.has(r.id)} onChange={() => toggle(r.id)} data-testid={`title-check-${i}`} /></td>
                  <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{`${r.first_name} ${r.last_name}`.trim() || "—"}{ratings[r.id] && <span className="ml-1.5 align-middle inline-block"><RatingBadge v={ratings[r.id]} /></span>}</td>
                  <td className="px-3 py-2 text-zinc-600 max-w-[240px] truncate" title={r.title}>{r.title || "—"}</td>
                  <td className="px-3 py-2 text-zinc-600 max-w-[200px] truncate" title={r.organization}>{r.organization || "—"}{r.location && <span className="text-zinc-400 text-xs"> · {r.location}</span>}</td>
                  <td className="px-3 py-2 font-mono text-[12.5px]">{r.revealed_email || <span className="text-zinc-400 font-sans text-xs">{r.has_email ? "hidden until reveal" : "—"}</span>}</td>
                  <td className="px-3 py-2">{r.linkedin && <a href={r.linkedin} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-sky-600" title="Open LinkedIn"><LinkedinLogo size={15} /></a>}</td>
                  <td className="px-3 py-2 text-xs">{statusCell(r)}</td>
                </tr>
              ))}
              {visible.length === 0 && !loading && <tr><td colSpan={7} className="p-8 text-center text-sm text-zinc-500">{rows.length > 0 ? "Everyone found is hidden (no email at Apollo or already in MediHub) — use the show link above." : "Nobody matches — try broader phrases or fewer filters."}</td></tr>}
            </tbody>
          </table>
          <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
            {rows.length > 0 && (
              <button data-testid="title-rate-btn" disabled={rateBusy || loading} onClick={rate}
                className="h-9 px-4 rounded-md border border-indigo-200 bg-white text-sm font-medium text-indigo-700 hover:border-indigo-400 disabled:opacity-40 flex items-center gap-1.5">
                <Brain size={14} /> {rateBusy ? "Rating…" : "Rate with AI (ICP)"}
              </button>
            )}
            {page * 100 < total && (
              <button onClick={loadMore} disabled={loading || importing} data-testid="title-load-more"
                className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 disabled:opacity-40">
                {loading ? "Loading…" : `Load 100 more (${Math.min(page * 100, total).toLocaleString()} of ${Number(total).toLocaleString()} scanned)`}
              </button>
            )}
            {sel.size > 0 && (
              <button onClick={doImport} disabled={importing || loading} data-testid="title-import-btn"
                className="ml-auto h-9 px-4 rounded-md bg-violet-700 text-white text-sm font-medium hover:bg-violet-800 disabled:opacity-40 flex items-center gap-1.5">
                <DownloadSimple size={14} />{importing ? "Revealing…" : `Reveal & import ${sel.size} (≈${rows.filter(r => sel.has(r.id) && !r.revealed_email).length} Apollo credit${rows.filter(r => sel.has(r.id) && !r.revealed_email).length === 1 ? "" : "s"})`}
              </button>
            )}
          </div>
          <p className="px-3 pb-3 text-xs text-zinc-400">Each person is imported with their own company name, source "apollo" and <span className="font-medium text-zinc-500">unverified</span> deliverability — run <Link to="/contacts" className="underline hover:text-zinc-600">Verify with Hunter in bulk</Link> before emailing. Your job-title exclusion list applies here too. Watch for hospital/clinic staff — they're respondents, not clients; the AI rating flags them N.</p>
        </div>
      )}
    </div>
  );
};
